import { HelloProduct, IBackground } from "../../types/backend";
import TopLineLoader from "../TopLineLoader";
import { AllEffects, Viewer, Web3d } from "hello-mono-sdk/packages/web-3d";
import {
  Coordinate3,
  EmitterType,
  RuleJsonType,
} from "hello-mono-sdk/packages/core";
import { MenuItem, ViewerRefType } from "../../types/frontend";
import { isDesktop, isMobile, isTablet } from "react-device-detect";
import MenuBar from "../MenuBar";
import StepsFlow from "../StepsFlow";
import { StepsFlowProps } from "../StepsFlow/StepsFlow";
import {
  onStepModeEnded,
  onStepModeModeStarted,
} from "../../utils/windowMessenger";
import React, {
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { findBgSettings } from "../../utils/appSettings";

interface IProps {
  product: HelloProduct;
  showMenu: boolean;
  muteAudio?: boolean;
  menuItems: MenuItem[];
  setSpecsVisible?: React.Dispatch<React.SetStateAction<boolean>>;
  setIsFeaturesActive: React.Dispatch<React.SetStateAction<boolean>>;
  specMode: string;
  onSceneLoaded?: () => void;
  onProgress?: (progress: number) => void;
  hideArButton: () => void;
  showArButton: () => void;
}

const DEFAULT_ENV_MAP = {
  map: "",
  name: "Neutral",
  intensity: 0.6,
  thumbnail: "https://dlz0qy8t8ufft.cloudfront.net/maps/png/photo_studio.jpg",
  format: ".png",
};

const findEffects = (product: HelloProduct): Array<AllEffects> => {
  let finalEffects: AllEffects[] = [];
  if (product.effects && product.effects.length > 0) {
    finalEffects = product.effects
      .filter((e) => e.enabled)
      .map((e) => ({ name: e.name, params: e.params }));
  }

  if (typeof product.application !== "string") {
    const remainingEffects = product.application.sdk_settings?.effects ?? [];
    for (let i = 0; i < remainingEffects.length; i++) {
      const e = remainingEffects[i];
      if (!e.enabled) {
        continue;
      }
      const effectAlreadyExists = product?.effects?.find(
        (fe) => fe.name === e.name
      );
      if (!effectAlreadyExists) {
        finalEffects.push({
          name: e.name,
          params: e.params,
        });
      }
    }
  }
  return finalEffects;
};

const Viewer3D = React.forwardRef<ViewerRefType, IProps>((props, ref) => {
  const {
    product,
    showMenu,
    muteAudio,
    menuItems,
    setSpecsVisible,
    specMode,
    hideArButton,
    showArButton,
    onSceneLoaded,
    onProgress,
    setIsFeaturesActive,
  } = props;
  const [is3dSceneLoading, setIs3dSceneLoading] = useState(true);
  const [sceneLoadingProgress, setSceneLoadingProgress] = useState(0);
  const [threeDeeError, setThreeDeeError] = useState<string>();
  const [isRunningRule, setIsRunningRule] = useState(false);
  const [isPostRenderingDone, setIsPostRenderingDone] = useState(false);
  const [emitterObject, setEmitterObject] = useState<EmitterType | undefined>(
    false
  );
  const [stepsFlowData, setStepsFlowData] = useState<
    StepsFlowProps | undefined
  >(undefined);
  const viewerRef = useRef<Viewer>();
  const isInitialized = useRef<boolean>(false);

  const destroy3D = useCallback(() => {
    viewerRef.current?.destroy();
    viewerRef.current = undefined;
    setIs3dSceneLoading(true);
    setSceneLoadingProgress(0);
    setThreeDeeError(undefined);
    setIsRunningRule(false);
    isInitialized.current = false;
  }, []);

  useEffect(() => {
    return () => {
      destroy3D();
    };
  }, []);

  useEffect(() => {
    if (viewerRef.current) {
      if (muteAudio) {
        viewerRef.current.getRuleManager().getAudioContext().muted = true;
      } else {
        viewerRef.current.getRuleManager().getAudioContext().muted = false;
      }
    }
  }, [muteAudio]);

  useImperativeHandle(
    ref,
    () => {
      return {
        runCustomRule: async (args: { rule: RuleJsonType, completionHandler?:any }) => {
          const manager = viewerRef.current?.getRuleManager();
          if (!manager) {
            return;
          }

          await viewerRef.current
            ?.getWeb3d()
            ?.getEffectsManager()
            .cancelTouchEffects();
          setIsRunningRule(true);
          manager.runCustomRule(args.rule, () => {
            setIsRunningRule(false);
            console.log(args, "Arguments")
            args?.completionHandler()
          });
        },
        getCore3D: () => {
          const core = viewerRef.current?.getWeb3d();
          return core;
        },
        getViewer: () => {
          const viewer = viewerRef.current;
          return viewer;
        }
      };
    },
    []
  );

  const onDivMounted = useCallback(
    async (div: HTMLDivElement) => {
      if (isInitialized.current) return;
      try {
        setThreeDeeError(undefined);
        setIs3dSceneLoading(true);
        let glbUrl: string | undefined = undefined;
        if (product.three_dee_model?.glbd?.length) {
          glbUrl = product.three_dee_model.glbd;
        } else if (product.three_dee_model?.glb?.length) {
          glbUrl = product.three_dee_model.glb;
        }
        let lights =
          product.rendering_rules?.lightsV2 ||
          product.rendering_rules?.metaLights ||
          [];
        let lightsToUpdatePosition: any[] = [];
        let envMapMeta =
          product.rendering_rules?.envMapV2 ||
          product.rendering_rules?.envMapMeta;
        let stringifedRules = product.rules.json;
        let rules: RuleJsonType[] = [];
        if (stringifedRules) {
          rules = JSON.parse(stringifedRules).rules;
        }

        const effects: AllEffects[] = findEffects(product);
        const background: IBackground = findBgSettings(product);

        if (glbUrl && div) {
          const viewer = new Viewer({ parent: div });
          const web3d = viewer.getWeb3d();
          const controlManager = web3d.getControlManager();
          const lightingManager = web3d.getLightingManager();
          viewerRef.current = viewer;
          const defaultLights = Web3d.getDefaultLights();
          if (lights.length === 0) {
            lights = defaultLights;
            lightsToUpdatePosition = defaultLights;
          }
          if (!envMapMeta) {
            envMapMeta = DEFAULT_ENV_MAP;
          }
          isInitialized.current = true;
          await viewer.init({
            modelDetails: {
              glb: glbUrl,
            },
            settings: {
              lights,
              envMapMeta,
              background: background,
              shadow: {
                enabled: true,
                intensity: product.rendering_rules?.shadowIntensity,
              },
            },
            effects: effects,
            rules: rules,
            onProgress: (progress) => {
              setSceneLoadingProgress(progress);
              onProgress && onProgress(progress);
            },
            onModelRendered: () => {
              const cameraOptions = product.rendering_rules?.camera;
              let initialCameraPosition: Coordinate3 | undefined = undefined;

              if (isDesktop) {
                initialCameraPosition = cameraOptions?.desktop;
              } else if (isMobile) {
                initialCameraPosition = cameraOptions?.mobile;
              } else if (isTablet) {
                initialCameraPosition = cameraOptions?.tablet;
              }

              if (initialCameraPosition) {
                const cameraManager = viewer.getWeb3d().getCameraManager();
                const controlsManager = viewer.getWeb3d().getControlManager();
                cameraManager.updateCamera({
                  position: initialCameraPosition,
                });
                controlsManager?.controls?.saveState();
              }

              let hasPostRenderRule = false;
              if (rules) {
                console.log("reached here,", rules);
                for (const rule of rules) {
                  if (rule.type === "PostRender") {
                    console.log("found postRender", rule);
                    hasPostRenderRule = true;
                    break;
                  }
                }
              }
              console.log(isPostRenderingDone, "POST RENDERING");
              console.log(is3dSceneLoading, "Scene RENDERING");
              if (!hasPostRenderRule) {
                console.log("EHRHRHRHRHRHR");
                setIsPostRenderingDone(true);
              }
              isPostRenderingDone && setIs3dSceneLoading(false);

              console.log(isPostRenderingDone, "POST RENDERING");
              console.log(is3dSceneLoading, "Scene RENDERING");
            },
            disableModelEntryAnimation:
              product?.rendering_rules?.animations?.disable,
          });

          if (muteAudio) {
            viewer.getRuleManager().getAudioContext().muted = true;
          } else {
            viewer.getRuleManager().getAudioContext().muted = false;
          }

          if (lightsToUpdatePosition.length > 0) {
            lightingManager.updateLightsPosition(lightsToUpdatePosition);
          }
          if (typeof product.application !== "string") {
            const maxCameraOrbit =
              product.application.sdk_settings?.model_viewer_settings
                ?.max_camera_orbit;
            const minCameraOrbit =
              product.application.sdk_settings?.model_viewer_settings
                ?.min_camera_orbit;

            if (maxCameraOrbit && minCameraOrbit) {
              const max = maxCameraOrbit.split(" ");
              const maxHorizontal = Number(max[0]);
              const maxVertical = Number(max[1]);

              const min = minCameraOrbit.split(" ");
              const minHorizontal = Number(min[0]);
              const minVertical = Number(min[1]);

              //anticlockwise is min
              //clockwise is max

              //Multiplying min values by -1 to use the same restriction function while using legacy appliction camera settings
              controlManager.restrictHorizontalRotation({
                clockwise: maxHorizontal,
                anticlockwise: minHorizontal * -1,
              });

              controlManager.restrictVerticalRotation({
                clockwise: maxVertical,
                anticlockwise: minVertical * -1,
              });
            }
          }

          const cameraOptions = product.rendering_rules?.camera;
          if (
            cameraOptions?.restrictHorizontalRotation &&
            cameraOptions?.restrictHorizontalRotation.enabled
          ) {
            controlManager.restrictHorizontalRotation(
              cameraOptions.restrictHorizontalRotation
            );
          }

          if (
            cameraOptions?.restrictVerticalRotation &&
            cameraOptions?.restrictVerticalRotation.enabled
          ) {
            controlManager.restrictVerticalRotation(
              cameraOptions.restrictVerticalRotation
            );
          }

          onSceneLoaded && onSceneLoaded();
          isPostRenderingDone && setIs3dSceneLoading(false);
          let emitter = viewer.getGlobalEmitter();
          setEmitterObject(emitter);

          emitter?.on("POST_RENDERING_COMPLETED", () => {
            setIsPostRenderingDone(true);
            setIs3dSceneLoading(false);
          });

          emitter?.on("STEPS_FLOW_STARTED", (data: any) => {
            setIsRunningRule(false);
            setSpecsVisible && setSpecsVisible(false);
            hideArButton();
            setIsFeaturesActive(true);
            setStepsFlowData({
              stepsData: data.stepsData,
              onCompleted: () => {
                setStepsFlowData(undefined);
                showArButton();
                onStepModeEnded();
                if (data && data.onCompleted) {
                  data.onCompleted();
                }
                setIsFeaturesActive(false);
              },
              ruleManager: viewer.getRuleManager(),
            });

            onStepModeModeStarted();
          });
        } else {
          setThreeDeeError("Cannot Initialize 3D Scene");
          console.error(
            "Cannot Initialize 3D Scene",
            "No GLB URL or Parent Div Found"
          );
        }
      } catch (err) {
        setThreeDeeError("Cannot Initialize 3D Scene");
        console.error("Cannot Initialize 3D Scene", err);
      }
    },
    [
      JSON.stringify(product),
      onSceneLoaded,
      onProgress,
      muteAudio,
      hideArButton,
      showArButton,
    ]
  );

  const bgData = useMemo(() => {
    return findBgSettings(product);
  }, [product]);

  const runRule = async (rule: RuleJsonType) => {
    const manager = viewerRef.current?.getRuleManager();
    if (!manager) {
      return;
    }
    setIsRunningRule(true);
    await viewerRef.current
      ?.getWeb3d()
      ?.getEffectsManager()
      .cancelTouchEffects();
    manager.runRuleById(rule.id, () => {
      setIsRunningRule(false);
    });
  };
  return (
    <>
      {is3dSceneLoading && !isPostRenderingDone && (
        <div className="loader-container">
          <div className="loader-containing-wrapper">
            <div className="loader-wrapper">
              <div className="loader-outline">
                <div
                  className="loader-progress"
                  style={{ width: `${sceneLoadingProgress}%` }}
                />
                <span className="loader-text">{sceneLoadingProgress}%</span>
              </div>
            </div>
          </div>
        </div>
      )}
      <div className="viewer full-width">
        {isRunningRule && <TopLineLoader />}
        {threeDeeError && <p>{threeDeeError}</p>}
        <div
          id="hello-div"
          ref={onDivMounted}
          style={{
            width: "100%",
            height: "99.5%",
          }}
        ></div>

        {stepsFlowData ? (
          <StepsFlow
            stepsData={stepsFlowData.stepsData}
            onCompleted={stepsFlowData.onCompleted}
            ruleManager={stepsFlowData.ruleManager}
            bgData={bgData}
          />
        ) : (
          <>
            {showMenu &&
              menuItems.length > 0 &&
              isPostRenderingDone &&
              emitterObject && (
                <MenuBar
                  menuItems={menuItems}
                  runRule={runRule}
                  isRuleRunning={isRunningRule}
                  specMode={specMode}
                  emitter={emitterObject}
                />
              )}
          </>
        )}
      </div>
    </>
  );
});

export default Viewer3D;

